import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Backspace as BackspaceIcon,
  Clear as ClearIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create a separate axios instance for kiosk (no JWT)
function createKioskApi(deviceToken) {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'X-Device-Token': deviceToken,
    },
  });
}

// Kiosk states
const STATES = {
  LOADING: 'loading',
  IDLE: 'idle',
  IDENTIFYING: 'identifying',
  CONFIRM: 'confirm',
  CHOOSE_COMPANY: 'choose_company',
  CHOOSE_SWITCH: 'choose_switch',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  DEVICE_ERROR: 'device_error',
};

export default function KioskMode() {
  const { kioskCode } = useParams();
  const [state, setState] = useState(STATES.LOADING);
  const [pin, setPin] = useState('');
  const [employee, setEmployee] = useState(null);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [serverTime, setServerTime] = useState('');
  const [siteName, setSiteName] = useState('');
  const [photoRequired, setPhotoRequired] = useState(false);
  // One kiosk can serve several properties; the crew picks which one they are
  // working before clocking in. Clock-OUT never asks — it closes whatever
  // shift is already open.
  // Pickable options: properties where a company has them, the company itself
  // where it does not. The crew member picks a building; the owning LLC is
  // derived server-side, so cost still rolls up per company.
  const [options, setOptions] = useState([]);
  const [defaultCompanyId, setDefaultCompanyId] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const kioskApi = useRef(null);
  const setupApi = useRef(axios.create({ baseURL: API_BASE_URL }));

  // Point the installable manifest at THIS kiosk, so pinning the tablet to the
  // home screen reopens the kiosk rather than hourtrax.com. Android launches an
  // installed site at the manifest's start_url, and the static manifest says
  // "/" — which discards both the tenant slug and the kiosk code.
  useEffect(() => {
    if (!kioskCode) return;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute('href');
    link.setAttribute('href', `/api/manifest/kiosk/${encodeURIComponent(kioskCode)}`);
    return () => { if (original) link.setAttribute('href', original); };
  }, [kioskCode]);

  // Setup: resolve short URL code to device token, then initialize API
  useEffect(() => {
    const doSetup = async () => {
      try {
        const res = await setupApi.current.get(`/kiosk/setup/${kioskCode}`);
        const { device_token, site_name, photo_required } = res.data;
        kioskApi.current = createKioskApi(device_token);
        setSiteName(site_name || '');
        setPhotoRequired(photo_required || false);

        // Properties this kiosk can punch against. A failure here is not fatal:
        // the kiosk falls back to the site's default company, which is the
        // behavior before the picker existed.
        try {
          const comp = await kioskApi.current.get('/kiosk/companies');
          setOptions(comp.data.options || []);
          setDefaultCompanyId(comp.data.default_company_id || null);
        } catch (compErr) {
          console.error('Kiosk company load failed:', compErr);
        }

        setState(STATES.IDLE);
      } catch (err) {
        console.error('Kiosk setup failed:', err);
        setErrorMessage(err.response?.data?.error || 'Kiosk not found or inactive');
        setState(STATES.DEVICE_ERROR);
      }
    };
    doSetup();
  }, [kioskCode]);

  // Heartbeat — runs after setup completes (kioskApi is initialized)
  useEffect(() => {
    if (state === STATES.LOADING || state === STATES.DEVICE_ERROR) return;

    const doHeartbeat = async () => {
      if (!kioskApi.current) return;
      try {
        const res = await kioskApi.current.get('/kiosk/heartbeat');
        setSiteName(res.data.site_name || '');
        setPhotoRequired(res.data.photo_required || false);
      } catch (err) {
        console.error('Heartbeat failed:', err);
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          setErrorMessage(err.response?.data?.error || 'Device deactivated or network changed');
          setState(STATES.DEVICE_ERROR);
        }
      }
    };

    heartbeatRef.current = setInterval(doHeartbeat, 60000);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [state]);

  // Clock display
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setServerTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    updateTime();
    timeIntervalRef.current = setInterval(updateTime, 1000);
    return () => clearInterval(timeIntervalRef.current);
  }, []);

  // Cleanup on unmount: stop camera, clear timeouts
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Wake lock to prevent screen sleep
  useEffect(() => {
    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.warn('Wake lock not available:', err);
      }
    };
    requestWakeLock();
    return () => { if (wakeLock) wakeLock.release(); };
  }, []);

  // Auto-reset timeout for success/error states
  const scheduleReset = useCallback((delayMs = 5000) => {
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(() => {
      resetToIdle();
    }, delayMs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetToIdle = useCallback(() => {
    setPin('');
    setEmployee(null);
    setMessage('');
    setErrorMessage('');
    stopCamera();
    setState(STATES.IDLE);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Camera management — use ref to avoid stale closure issues
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 320, height: 240 }
      });
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access denied:', err);
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // The <video> element lives inside the screen-specific JSX, so switching from
  // CONFIRM to CHOOSE_COMPANY remounts it with an empty srcObject. Re-bind the
  // still-running stream, or the selfie silently comes back null.
  useEffect(() => {
    if (videoRef.current && cameraStreamRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [state]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7);
  };

  // PIN input
  const handleDigit = (digit) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);

    // Auto-submit when 4 digits entered
    if (newPin.length === 4) {
      handleIdentify(newPin);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  // API calls
  const handleIdentify = async (pinValue) => {
    setState(STATES.IDENTIFYING);
    try {
      const res = await kioskApi.current.post('/kiosk/identify', { pin: pinValue });
      setEmployee(res.data);
      if (res.data.photo_required) {
        startCamera();
      }
      setState(STATES.CONFIRM);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Invalid PIN');
      setState(STATES.ERROR);
      scheduleReset(3000);
    }
  };

  // Ask which property first when the kiosk serves more than one; otherwise
  // go straight through with the single/default company.
  const handleClockIn = async () => {
    if (options.length > 1) {
      setState(STATES.CHOOSE_COMPANY);
      return;
    }
    await submitClockIn(options[0] || { company_id: defaultCompanyId, affiliate_id: null });
  };

  const submitClockIn = async (choice) => {
    setState(STATES.PROCESSING);
    try {
      let photo = null;
      if (photoRequired || employee?.photo_required) {
        photo = capturePhoto();
      }
      const res = await kioskApi.current.post('/kiosk/clock-in', {
        pin,
        photo_data: photo,
        company_id: choice?.company_id || undefined,
        affiliate_id: choice?.affiliate_id || undefined,
      });
      setMessage(res.data.message);
      stopCamera();
      setState(STATES.SUCCESS);
      scheduleReset(5000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Clock-in failed');
      stopCamera();
      setState(STATES.ERROR);
      scheduleReset(5000);
    }
  };

  // What THIS tablet serves: its site's company. `options` is every property in
  // the tenant, so testing against it made everyone look "already here" and
  // Switch To Here never appeared. Without a site company the tablet cannot
  // know where it is, so it falls back to the tenant list (and offers no switch).
  const servedOptions = defaultCompanyId
    ? options.filter((o) => o.company_id === defaultCompanyId)
    : options;
  const servesCurrent = (emp) =>
    servedOptions.some((o) =>
      o.affiliate_id
        ? o.affiliate_id === emp.affiliate_id
        : !emp.affiliate_id && o.company_id === emp.company_id
    ) || (servedOptions.length === 0 && emp.company_id === defaultCompanyId);
  const isElsewhere = Boolean(employee?.clocked_in) && employee && !servesCurrent(employee);

  // They started the day somewhere else and walked here. The target is this
  // tablet's own property, so there is nothing to pick unless the kiosk serves
  // several — and no ambiguity about where they physically are.
  const handleSwitch = async () => {
    if (servedOptions.length > 1) {
      setState(STATES.CHOOSE_SWITCH);
      return;
    }
    await submitSwitch(servedOptions[0] || { company_id: defaultCompanyId, affiliate_id: null });
  };

  const submitSwitch = async (choice) => {
    setState(STATES.PROCESSING);
    try {
      let photo = null;
      if (photoRequired || employee?.photo_required) {
        photo = capturePhoto();
      }
      const res = await kioskApi.current.post('/kiosk/switch', {
        pin,
        photo_data: photo,
        company_id: choice?.company_id || undefined,
        affiliate_id: choice?.affiliate_id || undefined,
      });
      setMessage(res.data.message);
      stopCamera();
      setState(STATES.SUCCESS);
      scheduleReset(5000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Switch failed');
      stopCamera();
      setState(STATES.ERROR);
      scheduleReset(5000);
    }
  };

  const handleBreak = async (action) => {
    setState(STATES.PROCESSING);
    try {
      const res = await kioskApi.current.post('/kiosk/break', { pin, action });
      setMessage(res.data.message);
      stopCamera();
      setState(STATES.SUCCESS);
      scheduleReset(4000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Break punch failed');
      stopCamera();
      setState(STATES.ERROR);
      scheduleReset(5000);
    }
  };

  const handleClockOut = async () => {
    setState(STATES.PROCESSING);
    try {
      let photo = null;
      if (photoRequired || employee?.photo_required) {
        photo = capturePhoto();
      }
      const res = await kioskApi.current.post('/kiosk/clock-out', {
        pin,
        photo_data: photo,
      });
      setMessage(res.data.message);
      stopCamera();
      setState(STATES.SUCCESS);
      scheduleReset(5000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Clock-out failed');
      stopCamera();
      setState(STATES.ERROR);
      scheduleReset(5000);
    }
  };

  // Keypad button style — uses viewport height units to always fit on screen
  const keypadBtnSx = {
    width: 'min(20vw, 8vh, 80px)',
    height: 'min(20vw, 8vh, 80px)',
    fontSize: 'min(4vw, 3vh, 1.8rem)',
    fontWeight: 'bold',
    borderRadius: 2,
    m: 'min(0.5vw, 0.4vh, 4px)',
    minWidth: 0,
    p: 0,
  };

  // --- RENDER ---

  // Device error (not registered)
  if (state === STATES.DEVICE_ERROR) {
    return (
      <Box sx={fullScreenSx}>
        <Box sx={contentWrapperSx}>
          <Alert severity="error" sx={{ fontSize: '1.2rem', maxWidth: 500 }}>
            {errorMessage}
          </Alert>
        </Box>
      </Box>
    );
  }

  // Loading
  if (state === STATES.LOADING) {
    return (
      <Box sx={fullScreenSx}>
        <Box sx={contentWrapperSx}>
          <CircularProgress size={60} />
          <Typography variant="h5" sx={{ mt: 3 }}>Connecting...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={fullScreenSx}>
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <Box sx={contentWrapperSx}>
      {/* Header — viewport-relative sizing to always fit */}
      <Box sx={{ textAlign: 'center', mb: 'min(1vh, 8px)' }}>
        <Typography sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: 'min(5vw, 4vh, 2.5rem)', lineHeight: 1.2 }}>
          HourTrax™
        </Typography>
        {siteName && (
          <Typography sx={{ color: 'text.secondary', fontSize: 'min(3vw, 2.5vh, 1rem)' }}>
            {siteName}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 'bold', color: 'text.primary', mt: 'min(1vh, 4px)', fontFamily: 'monospace', fontSize: 'min(8vw, 7vh, 3.5rem)', lineHeight: 1.2 }}>
          {serverTime}
        </Typography>
      </Box>

      {/* IDLE STATE — PIN Entry */}
      {state === STATES.IDLE && (
        <Paper elevation={4} sx={{ p: 'min(2vw, 2vh, 16px)', borderRadius: 2, maxWidth: 400, width: '100%' }}>
          <Typography align="center" sx={{ mb: 'min(1vh, 8px)', fontSize: 'min(4vw, 3vh, 1.3rem)', fontWeight: 500 }}>
            Enter your PIN
          </Typography>

          {/* PIN dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 'min(2vw, 1.5vh, 12px)', mb: 'min(1.5vh, 12px)' }}>
            {[0, 1, 2, 3].map(i => (
              <Box
                key={i}
                sx={{
                  width: 'min(5vw, 3vh, 24px)',
                  height: 'min(5vw, 3vh, 24px)',
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  backgroundColor: i < pin.length ? 'primary.main' : 'transparent',
                  transition: 'background-color 0.15s',
                }}
              />
            ))}
          </Box>

          {/* Numeric keypad */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {[[1, 2, 3], [4, 5, 6], [7, 8, 9]].map((row, ri) => (
              <Box key={ri} sx={{ display: 'flex' }}>
                {row.map(digit => (
                  <Button
                    key={digit}
                    variant="outlined"
                    onClick={() => handleDigit(String(digit))}
                    sx={keypadBtnSx}
                  >
                    {digit}
                  </Button>
                ))}
              </Box>
            ))}
            <Box sx={{ display: 'flex' }}>
              <Tooltip title="Clear">
                <IconButton aria-label="Clear PIN" onClick={handleClear} sx={{ ...keypadBtnSx, color: 'error.main' }}>
                  <ClearIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={() => handleDigit('0')}
                sx={keypadBtnSx}
              >
                0
              </Button>
              <Tooltip title="Backspace">
                <IconButton aria-label="Delete last digit" onClick={handleBackspace} sx={{ ...keypadBtnSx, color: 'text.secondary' }}>
                  <BackspaceIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      )}

      {/* IDENTIFYING — Spinner */}
      {state === STATES.IDENTIFYING && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={50} />
          <Typography variant="h6" sx={{ mt: 2 }}>Looking you up...</Typography>
        </Box>
      )}

      {/* CONFIRM — Show employee and action button */}
      {state === STATES.CONFIRM && employee && (
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, maxWidth: 500, width: '100%', textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {employee.first_name} {employee.last_name}
          </Typography>

          {employee.on_break && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              You are still on break. If you go back to work, tap <strong>End Break</strong> first —
              otherwise your break keeps running and your supervisor has to review the shift.
            </Alert>
          )}

          {employee.clocked_in ? (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {employee.on_break
                ? `On break since ${new Date(employee.break_started_at).toLocaleTimeString()}`
                : `Clocked in since ${new Date(employee.clock_in_time).toLocaleTimeString()}`}
              {employee.company_name && ` — ${employee.company_name}`}
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Not clocked in
            </Typography>
          )}

          {/* Camera preview */}
          {photoRequired && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid #ccc' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: 320, height: 240, objectFit: 'cover' }}
                />
                <CameraIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'white', opacity: 0.7 }} />
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              onClick={resetToIdle}
              sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
            >
              Cancel
            </Button>

            {employee.clocked_in ? (
              <>
                {employee.on_break ? (
                  <Button
                    variant="contained"
                    size="large"
                    color="warning"
                    onClick={() => handleBreak('end')}
                    sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                  >
                    End Break
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="large"
                    color="warning"
                    onClick={() => handleBreak('start')}
                    sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                  >
                    Start Break
                  </Button>
                )}
                {!employee.on_break && isElsewhere && (
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    onClick={handleSwitch}
                    sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                  >
                    Switch To Here
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  color="error"
                  onClick={handleClockOut}
                  sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                >
                  Clock Out
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                color="success"
                onClick={handleClockIn}
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
              >
                Clock In
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* CHOOSE_COMPANY — Which property are they working today?
          Only reached when the kiosk serves more than one. Big touch targets:
          this is a wall tablet used with work gloves on. */}
      {state === STATES.CHOOSE_COMPANY && employee && (
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, maxWidth: 620, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {employee.first_name}, which property?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your hours are recorded against the property you pick. The owner shown
            underneath is who the cost goes to.
          </Typography>

          {photoRequired && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid #ccc' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: 320, height: 240, objectFit: 'cover' }}
                />
                <CameraIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'white', opacity: 0.7 }} />
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mb: 3 }}>
            {options.map(o => (
              <Button
                key={`${o.company_id}-${o.affiliate_id || 'none'}`}
                variant={o.company_id === defaultCompanyId ? 'contained' : 'outlined'}
                size="large"
                onClick={() => submitClockIn(o)}
                sx={{
                  minWidth: 200,
                  py: 1.75,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  flexDirection: 'column',
                  lineHeight: 1.25,
                }}
              >
                {o.label}
                {o.group && (
                  <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.75 }}>
                    {o.group}
                  </Box>
                )}
              </Button>
            ))}
          </Box>

          <Button variant="text" size="large" onClick={resetToIdle} sx={{ px: 4 }}>
            Cancel
          </Button>
        </Paper>
      )}

      {/* CHOOSE_SWITCH — same picker, but they are already on the clock
          somewhere else, so the wording is about moving rather than starting. */}
      {state === STATES.CHOOSE_SWITCH && employee && (
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, maxWidth: 620, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {employee.first_name}, moving to which property?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your hours at {employee.affiliate_name || employee.company_name} are saved up to now,
            and the clock starts again here. You stay on the clock throughout.
          </Typography>

          {photoRequired && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid #ccc' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: 320, height: 240, objectFit: 'cover' }}
                />
                <CameraIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'white', opacity: 0.7 }} />
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mb: 3 }}>
            {servedOptions.map(o => (
              <Button
                key={`${o.company_id}-${o.affiliate_id || 'none'}`}
                variant={o.company_id === defaultCompanyId ? 'contained' : 'outlined'}
                size="large"
                onClick={() => submitSwitch(o)}
                sx={{
                  minWidth: 200,
                  py: 1.75,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  flexDirection: 'column',
                  lineHeight: 1.25,
                }}
              >
                {o.label}
                {o.group && (
                  <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.75 }}>
                    {o.group}
                  </Box>
                )}
              </Button>
            ))}
          </Box>

          <Button variant="text" size="large" onClick={resetToIdle} sx={{ px: 4 }}>
            Cancel
          </Button>
        </Paper>
      )}

      {/* PROCESSING — Spinner */}
      {state === STATES.PROCESSING && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={50} />
          <Typography variant="h6" sx={{ mt: 2 }}>Processing...</Typography>
        </Box>
      )}

      {/* SUCCESS */}
      {state === STATES.SUCCESS && (
        <Paper elevation={4} sx={{ p: 5, borderRadius: 3, maxWidth: 500, width: '100%', textAlign: 'center', bgcolor: 'success.soft' }}>
          <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold', mb: 2 }}>
            &#10003;
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            {message}
          </Typography>
        </Paper>
      )}

      {/* ERROR */}
      {state === STATES.ERROR && (
        <Paper elevation={4} sx={{ p: 5, borderRadius: 3, maxWidth: 500, width: '100%', textAlign: 'center', bgcolor: 'error.soft' }}>
          <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 'bold', mb: 2 }}>
            &#10007;
          </Typography>
          <Typography variant="h6" sx={{ color: 'error.main', mb: 3 }}>
            {errorMessage}
          </Typography>
          <Button variant="outlined" onClick={resetToIdle} sx={{ px: 4 }}>
            Try Again
          </Button>
        </Paper>
      )}

      </Box>

      {/* Footer — online status */}
      <Typography variant="caption" sx={{ position: 'fixed', bottom: 8, right: 16, color: 'text.disabled' }}>
        {navigator.onLine ? 'Connected' : 'Offline'}
      </Typography>
    </Box>
  );
}

// Full-screen container — fits viewport exactly, no scrolling
const fullScreenSx = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  bgcolor: 'background.default',
  // The kiosk renders outside the normal app layout, so nothing established a
  // text colour and everything inherited the teal accent — about 3:1 against
  // this background, well under the 4.5:1 needed to read a wall-mounted tablet
  // across a room. Accent belongs on the wordmark and status icons, not on
  // information. text.primary is ~16:1 here.
  color: 'text.primary',
  overflow: 'hidden',
};

// Inner content wrapper — fills viewport, centers content with flex
const contentWrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  px: { xs: 1, sm: 2 },
  py: { xs: 0.5, sm: 1 },
};
